import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Chip } from '@coordio/ui';
import type { AgentMessageDto } from '../../types/session';
import { MessageBubble } from './MessageBubble';
import { StreamingMessage } from './StreamingMessage';
import { ToolCallIndicator } from './ToolCallIndicator';
import { SubAgentCard } from '../orchestrator/SubAgentCard';
import type { OrchestratorEvent } from '../orchestrator/useOrchestratorStream';
import { signalRService } from '../../services/signalRService';
import type { SignalREvents } from '../../services/signalRService';
import { useSignalR } from '../../providers/SignalRProvider';
import { useNotificationStore } from '../../store/notificationStore';

interface MessageListWithStreamingProps {
  messages: AgentMessageDto[];
  sessionId: string;
  loading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onStreamingComplete?: (content: string) => void;
}

interface StreamingState {
  content: string;
  isStreaming: boolean;
  toolCalls: Array<{ id: string; name: string; arguments: string; status: 'pending' | 'executing' | 'completed' | 'failed' }>;
}

/**
 * Enhanced message list component with SignalR streaming support.
 */
export const MessageListWithStreaming = ({
  messages,
  sessionId,
  loading,
  onLoadMore,
  hasMore,
  onStreamingComplete,
}: MessageListWithStreamingProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [streamingState, setStreamingState] = useState<StreamingState | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('disconnected');
  const [isThinking, setIsThinking] = useState(false);
  const [subAgentEvents, setSubAgentEvents] = useState<OrchestratorEvent[]>([]);
  const subAgentEventIdRef = useRef(0);
  const { agentExecutionStatus } = useSignalR();
  const showNotification = useNotificationStore((s) => s.showNotification);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingState, scrollToBottom]);

  useEffect(() => {
    setConnectionStatus(agentExecutionStatus);

    if (agentExecutionStatus !== 'connected') {
      return;
    }

    // Join session and set up streaming events once connected.
    void signalRService.joinSession(sessionId).catch((error) => {
      console.error('Error joining SignalR session:', error);
      setConnectionStatus('disconnected');
    });

    const pushSubAgentEvent = (type: OrchestratorEvent['type'], callId: string, data: Record<string, unknown>) => {
      setSubAgentEvents((prev) => [
        ...prev,
        { id: `sa-${++subAgentEventIdRef.current}`, type, callId, data, timestamp: Date.now() },
      ]);
    };

    const events: SignalREvents = {
      onMessageReceived: (data) => {
        if (data.sessionId === sessionId) {
          setIsThinking(true);
        }
      },
      onAgentThinking: (data) => {
        if (data.sessionId === sessionId) {
          setIsThinking(true);
          setStreamingState({ content: '', isStreaming: true, toolCalls: [] });
        }
      },
      onTokenReceived: (data) => {
        if (data.sessionId === sessionId) {
          setIsThinking(false);
          setStreamingState((prev) => {
            if (!prev) {
              return { content: data.token, isStreaming: true, toolCalls: [] };
            }
            return { ...prev, content: prev.content + data.token, isStreaming: true };
          });
        }
      },
      onToolCall: (data) => {
        if (data.sessionId === sessionId) {
          setStreamingState((prev) => {
            const toolCall = {
              id: data.toolCallId || `tool-${Date.now()}`,
              name: data.toolName,
              arguments: data.arguments,
              status: 'executing' as const,
            };
            if (!prev) {
              return { content: '', isStreaming: true, toolCalls: [toolCall] };
            }
            return {
              ...prev,
              toolCalls: [...prev.toolCalls, toolCall],
            };
          });
        }
      },
      onToolResult: (data) => {
        if (data.sessionId === sessionId) {
          setStreamingState((prev) => {
            if (!prev) return prev;
            const status = data.success ? ('completed' as const) : ('failed' as const);
            let matched = false;
            const toolCalls = prev.toolCalls.map((tc) => {
              if (!matched && tc.id === data.toolCallId) {
                matched = true;
                return { ...tc, status };
              }
              return tc;
            });
            if (!matched) {
              // Fallback: flip the oldest still-executing call with the same tool name
              // (older ToolCall events may lack a toolCallId).
              const index = toolCalls.findIndex((tc) => tc.name === data.toolName && tc.status === 'executing');
              if (index >= 0) {
                toolCalls[index] = { ...toolCalls[index], status };
              }
            }
            return { ...prev, toolCalls };
          });
        }
      },
      onSubAgentStarted: (data) => {
        if (data.sessionId === sessionId) {
          pushSubAgentEvent('sub_agent_started', data.callId, {
            alias: data.alias,
            depth: data.depth,
            task: data.task,
          });
        }
      },
      onSubAgentTokenReceived: (data) => {
        if (data.sessionId === sessionId) {
          pushSubAgentEvent('sub_agent_token', data.callId, { token: data.token });
        }
      },
      onSubAgentToolCall: (data) => {
        if (data.sessionId === sessionId) {
          pushSubAgentEvent('sub_agent_tool_call', data.callId, {
            toolName: data.toolName,
            arguments: data.arguments,
          });
        }
      },
      onSubAgentCompleted: (data) => {
        if (data.sessionId === sessionId) {
          pushSubAgentEvent('sub_agent_completed', data.callId, {
            result: data.result,
            success: data.success,
            errorMessage: data.errorMessage,
          });
        }
      },
      onResponseComplete: (data) => {
        if (data.sessionId === sessionId) {
          setIsThinking(false);
          onStreamingComplete?.(data.response ?? '');
          setStreamingState(null);
          setSubAgentEvents([]);
        }
      },
      onExecutionError: (data) => {
        if (data.sessionId !== sessionId) {
          return;
        }
        // Surface the backend error to the user as a top toast notification
        // so they know their message failed instead of silently losing the
        // "thinking" indicator.
        const message = data.errorMessage?.trim()
          ? data.errorMessage
          : 'An error occurred while processing your message.';
        showNotification(message, 'error', 8000);

        // Clear in-flight streaming UI; the matching ResponseComplete event
        // (sent by the server right after) will also reload messages.
        setIsThinking(false);
        setStreamingState(null);
        setSubAgentEvents([]);
      },
    };

    signalRService.on(events);

    return () => {
      signalRService.leaveSession(sessionId);
      signalRService.off();
    };
  }, [agentExecutionStatus, sessionId, onStreamingComplete, showNotification]);

  const handleScroll = () => {
    if (!containerRef.current || !onLoadMore || !hasMore || loading) return;

    const { scrollTop } = containerRef.current;
    if (scrollTop < 100) {
      onLoadMore();
    }
  };

  // Deduplicate messages by ID to prevent rendering the same message multiple times
  const uniqueMessages = useMemo(() => {
    const seen = new Set<string>();
    return messages.filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [messages]);

  const subAgentGroups = useMemo(() => {
    const map = new Map<string, OrchestratorEvent[]>();
    const topLevelCallIds: string[] = [];
    for (const evt of subAgentEvents) {
      if (!evt.callId) continue;
      let group = map.get(evt.callId);
      if (!group) {
        group = [];
        map.set(evt.callId, group);
        topLevelCallIds.push(evt.callId);
      }
      group.push(evt);
    }
    return { map, topLevelCallIds };
  }, [subAgentEvents]);

  if (uniqueMessages.length === 0 && !loading && !streamingState) {
    return (
      <div className="flex items-center justify-center h-full p-8 flex-col gap-4">
        <p className="text-base text-muted-foreground">
          No messages yet. Start the conversation!
        </p>
        {connectionStatus !== 'connected' && (
          <Chip
            label={connectionStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}
            color={connectionStatus === 'connecting' ? 'amber' : 'red'}
          />
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto p-4 flex flex-col"
    >
      {/* Connection Status Indicator */}
      {connectionStatus !== 'connected' && (
        <div className="mb-2 flex justify-center">
          <Chip
            label={connectionStatus === 'connecting' ? 'Connecting to real-time updates...' : 'Real-time updates disconnected'}
            color={connectionStatus === 'connecting' ? 'amber' : 'red'}
          />
        </div>
      )}

      {loading && uniqueMessages.length === 0 && (
        <div className="flex justify-center p-4">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}
      {hasMore && (
        <div className="flex justify-center p-2">
          <span className="text-xs text-muted-foreground">
            Scroll up to load more messages...
          </span>
        </div>
      )}
      {uniqueMessages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
        />
      ))}
      
      {/* Streaming Message */}
      {streamingState && (
        <div className="mb-4">
          {streamingState.toolCalls.length > 0 && (
            <div className="mb-2">
              {streamingState.toolCalls.map((toolCall) => (
                <ToolCallIndicator
                  key={toolCall.id}
                  toolName={toolCall.name}
                  arguments={toolCall.arguments}
                  status={toolCall.status}
                />
              ))}
            </div>
          )}

          {subAgentGroups.topLevelCallIds.length > 0 && (
            <div className="mb-2">
              {subAgentGroups.topLevelCallIds.map((callId) => (
                <SubAgentCard
                  key={callId}
                  callId={callId}
                  events={subAgentGroups.map.get(callId) ?? []}
                  allEvents={subAgentGroups.map}
                />
              ))}
            </div>
          )}

          <StreamingMessage
            content={streamingState.content}
            isStreaming={streamingState.isStreaming || isThinking}
          />
        </div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
};
